import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Heart,
  MessageCircle,
  Share2,
  Play,
  Pause,
  Volume2,
  VolumeX,
  Sparkles,
  Check,
  TrendingUp,
  MapPin,
  ShieldCheck,
  Briefcase,
  UserCircle2,
  Video,
  Handshake,
  ArrowRight,
  Flame,
} from 'lucide-react';
import AnimatedNumber from './AnimatedNumber';
import { playSparkSound } from '../lib/sparkSound';

// ============== Phase machine (loops every ~13s) ==============
// 0: idle (entrepreneur posting / investor browsing) — 4.2s
// 1: investor clicks "Tenho Interesse" — beam + entrepreneur sees offer — 3.5s
// 2: entrepreneur accepts ("Aceitar Spark") — 2.0s
// 3: DEU SPARK celebration overlay — 3.0s
const PHASE_DURATIONS = [4200, 3500, 2000, 3000];

const useSparkPhase = () => {
  const [phase, setPhase] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setPhase((p) => (p + 1) % PHASE_DURATIONS.length), PHASE_DURATIONS[phase]);
    return () => clearTimeout(t);
  }, [phase]);
  return phase;
};

const SparkExperienceSection = () => {
  const phase = useSparkPhase();
  const [soundOn, setSoundOn] = useState(false);
  const prevPhaseRef = useRef(phase);

  // Trigger spark sound when entering phase 3 (only if soundOn)
  useEffect(() => {
    if (soundOn && phase === 3 && prevPhaseRef.current !== 3) {
      playSparkSound().catch(() => {});
    }
    prevPhaseRef.current = phase;
  }, [phase, soundOn]);

  const handleToggleSound = async () => {
    const next = !soundOn;
    setSoundOn(next);
    if (next) {
      // Immediately play once on user gesture (unlocks audio context)
      playSparkSound().catch(() => {});
    }
  };

  return (
    <section id="experience" className="relative py-24 lg:py-32 overflow-hidden">
      {/* Soft aurora */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[700px] rounded-full"
          style={{ background: 'radial-gradient(ellipse, rgba(79,172,254,0.10), transparent 60%)' }} />
        <div className="absolute top-1/3 right-0 w-[500px] h-[500px] rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(255,167,38,0.12), transparent 70%)' }} />
      </div>

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl mx-auto text-center"
        >
          <span className="eyebrow eyebrow-gold">
            <Sparkles size={14} />
            Veja uma Spark acontecendo
          </span>
          <h2 className="mt-5 heading-display text-3xl sm:text-4xl lg:text-5xl text-white">
            Quando os dois se conectam, <span className="gradient-text">nasce uma Spark</span>.
          </h2>
          <p className="mt-5 text-base sm:text-lg text-slate-400 leading-relaxed">
            Empreendedores mostram seus negócios. Investidores descobrem oportunidades.
            Quando dá Spark — a faísca acontece.
          </p>

          {/* Sound toggle */}
          <button
            onClick={handleToggleSound}
            className="mt-6 inline-flex items-center gap-2 text-xs text-slate-300 hover:text-white transition-colors px-4 py-2 rounded-full border border-white/10 hover:border-[#ffa726]/50 bg-white/[0.03] backdrop-blur-md"
            aria-label="Ativar som da Spark"
          >
            {soundOn ? <Volume2 size={14} className="text-[#ffa726]" /> : <VolumeX size={14} />}
            {soundOn ? "Som ativo · você vai ouvir 'Deu Spark!'" : "🔊 Ouvir 'Deu Spark!'"}
          </button>
        </motion.div>

        {/* Stage */}
        <div className="relative mt-16 lg:mt-20">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-6 items-center relative">
            {/* LEFT — Phone with TikTok-style reel */}
            <PhoneReel phase={phase} />

            {/* Connection beam (desktop only) */}
            <ConnectionBeam phase={phase} />

            {/* RIGHT — Investor panel */}
            <InvestorPanel phase={phase} />
          </div>

          {/* SPARK celebration overlay */}
          <AnimatePresence>{phase === 3 && <SparkOverlay />}</AnimatePresence>
        </div>

        {/* 3 simple steps */}
        <Steps />
      </div>
    </section>
  );
};

// ============== Phone Reel (TikTok-like) ==============
const PhoneReel = ({ phase }) => {
  const [liked, setLiked] = useState(false);
  const [likes, setLikes] = useState(2847);

  useEffect(() => {
    // Pulse likes increment continuously
    const id = setInterval(() => setLikes((l) => l + Math.floor(Math.random() * 3) + 1), 1800);
    return () => clearInterval(id);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30, rotate: -2 }}
      whileInView={{ opacity: 1, y: 0, rotate: -2 }}
      viewport={{ once: true, margin: '-100px' }}
      transition={{ duration: 0.9 }}
      className="relative mx-auto w-full max-w-[320px] lg:max-w-[340px]"
    >
      {/* Behind glow */}
      <div className="absolute -inset-10 bg-gradient-to-br from-[#ff7043]/25 via-transparent to-[#4facfe]/20 rounded-full blur-3xl" />

      {/* Phone frame */}
      <div className="relative bg-gradient-to-b from-[#1a1f3a] to-[#0a0e1f] rounded-[2.5rem] p-2 border border-white/10 shadow-2xl glow-mix">
        <div className="relative bg-[#050816] rounded-[2rem] overflow-hidden w-full aspect-[9/19]">
          {/* Notch */}
          <div className="absolute top-2 left-1/2 -translate-x-1/2 w-24 h-5 bg-black rounded-full z-30" />

          {/* Status bar */}
          <div className="absolute top-3 left-0 right-0 px-6 flex items-center justify-between text-[9px] text-white/80 font-medium z-20">
            <span>9:41</span>
            <div className="flex items-center gap-1">
              <div className="w-3 h-1.5 rounded-sm bg-white/80" />
              <div className="w-1.5 h-1.5 rounded-full bg-white/80" />
            </div>
          </div>

          {/* "Video" content — entrepreneur reel */}
          <ReelVideo />

          {/* Right side action rail (like, comment, share) */}
          <div className="absolute right-3 bottom-32 z-20 flex flex-col items-center gap-5">
            <button
              onClick={() => { setLiked((v) => !v); setLikes((l) => l + (liked ? -1 : 1)); }}
              className="flex flex-col items-center"
              aria-label="like"
            >
              <motion.div
                animate={liked ? { scale: [1, 1.35, 1] } : {}}
                transition={{ duration: 0.4 }}
                className={`w-11 h-11 rounded-full backdrop-blur-md flex items-center justify-center ${
                  liked ? 'bg-red-500/30' : 'bg-white/10'
                }`}
              >
                <Heart
                  size={20}
                  className={liked ? 'fill-red-500 text-red-500' : 'text-white'}
                />
              </motion.div>
              <span className="text-[10px] text-white mt-1 font-semibold">
                {likes.toLocaleString('pt-BR')}
              </span>
            </button>

            <button className="flex flex-col items-center" aria-label="comments">
              <div className="w-11 h-11 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center">
                <MessageCircle size={20} className="text-white" />
              </div>
              <span className="text-[10px] text-white mt-1 font-semibold">182</span>
            </button>

            <button className="flex flex-col items-center" aria-label="share">
              <div className="w-11 h-11 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center">
                <Share2 size={18} className="text-white" />
              </div>
              <span className="text-[10px] text-white mt-1 font-semibold">94</span>
            </button>
          </div>

          {/* Floating reactions */}
          <FloatingReactions />

          {/* Bottom caption */}
          <div className="absolute bottom-0 left-0 right-0 px-4 pb-4 pt-10 z-20"
            style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.85), transparent)' }}
          >
            <div className="flex items-center gap-2 mb-2">
              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#ffa726] to-[#ff7043] flex items-center justify-center text-[10px] font-bold text-black ring-2 ring-white">
                B
              </div>
              <div className="text-xs font-semibold text-white">@beatriz_cafeteria</div>
              <div className="text-[10px] text-[#4facfe] font-medium border border-[#4facfe]/40 rounded-full px-2 py-0.5 bg-[#4facfe]/10">
                Seguir
              </div>
            </div>
            <p className="text-[11px] text-white leading-snug">
              Abri minha cafeteria há 1 ano. Quero abrir mais 2 unidades. ☕✨
              <span className="text-[#4facfe]"> #procurandoinvestidor #cafeteria</span>
            </p>

            {/* "Procurando Investidor" CTA inside reel */}
            <motion.div
              animate={{ y: [0, -4, 0] }}
              transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
              className="mt-3 inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-[10px] font-bold text-white"
              style={{
                background: 'linear-gradient(135deg, #4facfe, #1e90ff)',
                boxShadow: '0 0 24px rgba(79,172,254,0.6)',
              }}
            >
              <Briefcase size={11} />
              Procurando investidor · R$ 80.000
            </motion.div>

            {/* Progress bar (video timeline) */}
            <div className="mt-3 h-0.5 rounded-full bg-white/10 overflow-hidden">
              <motion.div
                key="bar"
                animate={{ width: ['0%', '100%'] }}
                transition={{ duration: 6, repeat: Infinity, ease: 'linear' }}
                className="h-full rounded-full"
                style={{ background: 'linear-gradient(90deg, #ffa726, #ff7043)' }}
              />
            </div>
          </div>

          {/* Incoming Spark request overlay (phase 1) */}
          <AnimatePresence>
            {phase === 1 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.5 }}
                className="absolute inset-x-3 top-16 z-40"
              >
                <div className="relative rounded-2xl p-3 pr-3.5"
                  style={{
                    background: 'rgba(20, 25, 50, 0.85)',
                    border: '1px solid rgba(79, 172, 254, 0.45)',
                    backdropFilter: 'blur(24px)',
                    WebkitBackdropFilter: 'blur(24px)',
                    boxShadow: '0 14px 40px rgba(79,172,254,0.4)',
                  }}
                >
                  <motion.div
                    animate={{ opacity: [0.4, 1, 0.4] }}
                    transition={{ duration: 1.8, repeat: Infinity }}
                    className="absolute inset-0 rounded-2xl pointer-events-none"
                    style={{ boxShadow: '0 0 28px rgba(79,172,254,0.4)' }}
                  />
                  <div className="relative flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#4facfe] to-[#1e90ff] flex items-center justify-center text-white">
                      ⚡
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] font-bold text-[#4facfe] uppercase tracking-wider">Nova Spark</div>
                      <div className="text-[11px] text-white leading-tight font-semibold">Roberto C. tem interesse no seu negócio</div>
                    </div>
                  </div>
                  <div className="mt-2.5 grid grid-cols-2 gap-2">
                    <button className="rounded-lg py-2 text-[10px] font-bold text-white"
                      style={{
                        background: 'linear-gradient(135deg, #ffa726, #ff7043)',
                        boxShadow: '0 6px 16px rgba(255,112,67,0.4)',
                      }}
                    >
                      ⚡ Aceitar Spark
                    </button>
                    <button className="rounded-lg py-2 text-[10px] font-semibold text-slate-300 border border-white/15 bg-white/5">
                      Recusar
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* "ACEITO!" tap-feedback (phase 2) */}
          <AnimatePresence>
            {phase === 2 && (
              <motion.div
                initial={{ scale: 0.7, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 1.4, opacity: 0 }}
                transition={{ duration: 0.5 }}
                className="absolute inset-0 flex items-center justify-center z-40"
              >
                <div className="rounded-full px-5 py-3 text-sm font-extrabold text-black flex items-center gap-2"
                  style={{
                    background: 'linear-gradient(135deg, #ffd54f, #ffa726, #ff7043)',
                    boxShadow: '0 0 60px rgba(255,167,38,0.7), 0 0 100px rgba(255,112,67,0.4)',
                  }}
                >
                  ⚡ ACEITEI! ⚡
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Floating comment bubble */}
      <motion.div
        initial={{ opacity: 0, x: -30, y: 10 }}
        whileInView={{ opacity: 1, x: 0, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.5 }}
        className="absolute -left-4 sm:-left-12 top-1/4 glass-card rounded-2xl px-3 py-2 max-w-[180px] glow-blue float-slow z-30"
      >
        <div className="flex items-center gap-1.5 mb-1">
          <div className="w-5 h-5 rounded-full bg-gradient-to-br from-[#4facfe] to-[#1e90ff] flex items-center justify-center text-[8px] font-bold text-white">
            R
          </div>
          <span className="text-[9px] text-white font-semibold">Roberto C.</span>
        </div>
        <p className="text-[10px] text-slate-300 leading-tight">"Adorei sua ideia! Quero conversar 🙌"</p>
      </motion.div>

      {/* Floating likes label */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.8 }}
        className="absolute -top-3 -right-3 sm:-right-6 glass-card-gold rounded-full px-3 py-1.5 flex items-center gap-1.5 glow-gold float-med z-30"
      >
        <TrendingUp size={11} className="text-[#ffd54f]" />
        <span className="text-[10px] font-bold text-white">+312 views</span>
      </motion.div>
    </motion.div>
  );
};

const ReelVideo = () => {
  // Stylized animated "video" representation
  return (
    <div className="absolute inset-0">
      {/* Background image of small business / coffee shop vibe */}
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1554118811-1e0d58224f24?w=600&q=80"
          alt="Cafeteria"
          className="w-full h-full object-cover opacity-90"
        />
        {/* Cinematic gradient overlay */}
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(180deg, rgba(0,0,0,0.25) 0%, transparent 30%, transparent 60%, rgba(0,0,0,0.7) 100%)'
        }} />
        {/* Warm glow accents */}
        <div className="absolute top-10 left-6 w-20 h-20 rounded-full bg-[#ffa726]/40 blur-2xl" />
        <div className="absolute bottom-32 right-10 w-24 h-24 rounded-full bg-[#4facfe]/30 blur-2xl" />
      </div>

      {/* Mini chart floating */}
      <motion.div
        animate={{ y: [0, -6, 0] }}
        transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
        className="absolute top-16 left-3 z-10 glass-card rounded-xl px-2.5 py-2"
      >
        <div className="text-[8px] uppercase tracking-widest text-slate-300">Vendas/mês</div>
        <div className="text-sm font-bold text-white leading-tight">crescendo</div>
        <svg viewBox="0 0 60 18" className="w-14 h-4 mt-1">
          <path
            d="M 0 14 L 12 10 L 24 12 L 36 6 L 48 8 L 60 2"
            fill="none"
            stroke="#4facfe"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      </motion.div>

      {/* Play indicator pulsing in center */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <motion.div
          animate={{ scale: [1, 1.08, 1], opacity: [0.6, 1, 0.6] }}
          transition={{ duration: 2.5, repeat: Infinity }}
          className="w-14 h-14 rounded-full bg-white/15 backdrop-blur-md flex items-center justify-center ring-2 ring-white/30"
        >
          <Play size={20} className="text-white fill-white ml-0.5" />
        </motion.div>
      </div>
    </div>
  );
};

const FloatingReactions = () => {
  const reactions = ['❤️', '🔥', '✨', '👏', '💜', '🚀'];
  return (
    <div className="absolute inset-0 pointer-events-none z-10 overflow-hidden">
      {reactions.map((r, i) => (
        <motion.div
          key={i}
          initial={{ y: 400, opacity: 0, x: 240 + (i * 5) }}
          animate={{
            y: -50,
            opacity: [0, 1, 1, 0],
            x: 240 + Math.sin(i) * 30,
          }}
          transition={{
            duration: 5 + (i % 3),
            repeat: Infinity,
            delay: i * 0.9,
            ease: 'easeOut',
          }}
          className="absolute text-xl"
        >
          {r}
        </motion.div>
      ))}
    </div>
  );
};

// ============== Connection Beam ==============
const ConnectionBeam = ({ phase }) => {
  return (
    <div className="hidden lg:block pointer-events-none absolute inset-0 z-10">
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 800 500" preserveAspectRatio="none">
        <defs>
          <linearGradient id="beam-grad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#4facfe" />
            <stop offset="50%" stopColor="#ffffff" />
            <stop offset="100%" stopColor="#ffa726" />
          </linearGradient>
          <filter id="beam-glow">
            <feGaussianBlur stdDeviation="4" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        {/* Static thin dashed background line */}
        <path
          d="M 220 250 Q 400 220 580 250"
          fill="none"
          stroke="rgba(255,255,255,0.08)"
          strokeWidth="1"
          strokeDasharray="3 6"
        />
        {/* Active animated beam during phase 1-3 + glow */}
        <AnimatePresence>
          {phase >= 1 && (
            <motion.path
              key="active-beam"
              d="M 220 250 Q 400 220 580 250"
              fill="none"
              stroke="url(#beam-grad)"
              strokeWidth="2.5"
              filter="url(#beam-glow)"
              strokeLinecap="round"
              initial={{ pathLength: 0, opacity: 0 }}
              animate={{ pathLength: 1, opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.4, ease: 'easeInOut' }}
            />
          )}
        </AnimatePresence>
        {/* Traveling dot during phase 1 */}
        <AnimatePresence>
          {phase === 1 && (
            <motion.circle
              key="traveler"
              r="6"
              fill="#ffffff"
              filter="url(#beam-glow)"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <animateMotion
                dur="1.4s"
                repeatCount="1"
                path="M 220 250 Q 400 220 580 250"
              />
            </motion.circle>
          )}
        </AnimatePresence>
      </svg>

      {/* Center pulse */}
      <AnimatePresence>
        {phase >= 1 && (
          <motion.div
            key="center-pulse"
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.5 }}
            transition={{ duration: 0.5 }}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
          >
            <div className="relative">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#4facfe] to-[#ffa726] flex items-center justify-center glow-mix">
                <Handshake size={22} className="text-white" />
              </div>
              <motion.div
                animate={{ scale: [1, 1.8], opacity: [0.6, 0] }}
                transition={{ duration: 1.6, repeat: Infinity }}
                className="absolute inset-0 rounded-full bg-gradient-to-br from-[#4facfe] to-[#ffa726]"
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ============== Investor Panel ==============
const InvestorPanel = ({ phase }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-100px' }}
      transition={{ duration: 0.9, delay: 0.1 }}
      className="relative max-w-md mx-auto w-full"
    >
      {/* Glow */}
      <div className="absolute -inset-6 bg-gradient-to-br from-[#4facfe]/20 to-[#1e90ff]/5 rounded-3xl blur-2xl" />

      <div className="relative glass-card-blue rounded-3xl p-5 sm:p-6 glow-blue">
        {/* Top bar */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2.5">
            <div className="relative w-10 h-10 rounded-full overflow-hidden ring-2 ring-[#4facfe]/60"
              style={{ boxShadow: '0 0 20px rgba(79,172,254,0.5)' }}>
              <img
                src="https://images.unsplash.com/photo-1560250097-0b93528c311a?w=200&h=200&fit=crop&crop=face&q=80"
                alt="Investidor"
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#4facfe]">Investidor</div>
              <div className="text-sm font-bold text-white leading-tight">Roberto Cavalcanti</div>
            </div>
          </div>
          <div className="flex items-center gap-1.5 text-[10px] text-green-400 bg-green-400/10 rounded-full px-2.5 py-1">
            <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
            Online
          </div>
        </div>

        {/* Section label */}
        <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-3 flex items-center gap-2">
          <Sparkles size={11} className="text-[#ffa726]" />
          Oportunidade pra você
        </div>

        {/* Opportunity card */}
        <motion.div
          animate={phase === 1 ? { scale: [1, 1.02, 1], borderColor: ['rgba(79,172,254,0.2)', 'rgba(255,167,38,0.7)', 'rgba(79,172,254,0.2)'] } : {}}
          transition={{ duration: 1.2 }}
          className="relative rounded-2xl p-4 mb-4 overflow-hidden"
          style={{
            background: 'linear-gradient(145deg, rgba(255,167,38,0.08), rgba(10,14,30,0.5))',
            border: '1px solid rgba(79,172,254,0.2)',
          }}
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-xl overflow-hidden ring-1 ring-white/10">
              <img
                src="https://images.unsplash.com/photo-1554118811-1e0d58224f24?w=200&q=80"
                alt="Cafeteria"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex-1">
              <div className="text-sm font-bold text-white">Cafeteria Beatriz</div>
              <div className="flex items-center gap-1 text-[10px] text-slate-400 mt-0.5">
                <MapPin size={10} /> São Paulo · SP
              </div>
            </div>
            <div className="text-right">
              <div className="text-[9px] uppercase tracking-widest text-slate-400">Spark</div>
              <div className="text-base font-bold gradient-text">96%</div>
            </div>
          </div>

          <p className="text-[11px] text-slate-300 leading-snug mb-3">
            Quero expandir minha cafeteria com 2 novas unidades no centro de SP.
          </p>

          {/* Mini growth chart */}
          <div className="rounded-lg p-2.5 mb-3" style={{ background: 'rgba(255,255,255,0.03)' }}>
            <div className="flex items-center justify-between text-[9px] text-slate-400 mb-1">
              <span>Crescimento</span>
              <span className="text-green-400 font-semibold">consistente</span>
            </div>
            <svg viewBox="0 0 100 30" className="w-full h-8">
              <defs>
                <linearGradient id="inv-area" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#4facfe" stopOpacity="0.4" />
                  <stop offset="100%" stopColor="#4facfe" stopOpacity="0" />
                </linearGradient>
              </defs>
              <motion.path
                initial={{ pathLength: 0 }}
                whileInView={{ pathLength: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 1.4 }}
                d="M 0 24 L 12 22 L 24 18 L 36 20 L 48 14 L 60 12 L 72 8 L 84 6 L 100 2"
                fill="none"
                stroke="#4facfe"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              <path
                d="M 0 24 L 12 22 L 24 18 L 36 20 L 48 14 L 60 12 L 72 8 L 84 6 L 100 2 L 100 30 L 0 30 Z"
                fill="url(#inv-area)"
              />
            </svg>
          </div>

          {/* Tags */}
          <div className="flex flex-wrap gap-1.5 mb-3">
            {['Cafeteria', 'Alimentação', 'R$ 80.000'].map((t, i) => (
              <span key={i} className="text-[9px] text-slate-300 bg-white/5 border border-white/10 rounded-full px-2 py-0.5">
                {t}
              </span>
            ))}
          </div>

          {/* CTA — also navigates to signup as investor */}
          <Link
            to="/auth?signup=1&role=investor"
            data-testid="spark-experience-cta"
            className={`relative w-full rounded-xl py-3 text-xs font-bold flex items-center justify-center gap-2 transition-all overflow-hidden ${
              phase >= 1 ? 'btn-gold' : 'btn-primary'
            }`}
          >
            <AnimatePresence mode="wait">
              {phase < 1 ? (
                <motion.span key="interest" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex items-center gap-2">
                  <Heart size={14} />
                  Tenho Interesse
                </motion.span>
              ) : phase < 3 ? (
                <motion.span key="sent" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex items-center gap-2">
                  <Check size={14} />
                  Aguardando resposta...
                </motion.span>
              ) : (
                <motion.span key="spark" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex items-center gap-2">
                  <Sparkles size={14} />
                  ⚡ Deu Spark!
                </motion.span>
              )}
            </AnimatePresence>
          </Link>
        </motion.div>

        {/* Trust footer */}
        <div className="flex items-center justify-between text-[10px] text-slate-400">
          <div className="flex items-center gap-1.5">
            <ShieldCheck size={12} className="text-[#4facfe]" /> Perfil verificado
          </div>
          <div className="flex items-center gap-1.5">
            <UserCircle2 size={12} className="text-[#ffa726]" /> 8 conexões
          </div>
        </div>
      </div>

      {/* Floating mini-notification */}
      <motion.div
        initial={{ opacity: 0, x: 30 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.7 }}
        className="absolute -bottom-4 -right-3 sm:-right-8 glass-card rounded-2xl px-3 py-2 flex items-center gap-2 glow-blue float-slow z-30"
      >
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#4facfe] to-[#ffa726] flex items-center justify-center">
          <Sparkles size={14} className="text-white" />
        </div>
        <div>
          <div className="text-[9px] text-slate-400 uppercase tracking-wider">Spark</div>
          <div className="text-[11px] font-bold text-white">96% compatível</div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ============== Spark celebration overlay ==============
const SparkOverlay = () => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      className="absolute inset-0 flex items-center justify-center pointer-events-none z-40"
    >
      {/* Radial vignette */}
      <div className="absolute inset-0" style={{
        background: 'radial-gradient(circle at center, rgba(5,8,22,0.6) 0%, transparent 60%)'
      }} />

      {/* Particles */}
      {Array.from({ length: 22 }).map((_, i) => {
        const angle = (i / 22) * Math.PI * 2;
        const dist = 180 + Math.random() * 80;
        const tx = Math.cos(angle) * dist;
        const ty = Math.sin(angle) * dist;
        const isGold = i % 2 === 0;
        return (
          <motion.div
            key={i}
            initial={{ x: 0, y: 0, opacity: 0, scale: 0 }}
            animate={{ x: tx, y: ty, opacity: [0, 1, 0], scale: [0, 1, 0] }}
            transition={{ duration: 1.6, delay: 0.2 + (i % 6) * 0.04, ease: 'easeOut' }}
            className="absolute w-1.5 h-1.5 rounded-full"
            style={{
              background: isGold ? '#ffa726' : '#4facfe',
              boxShadow: isGold ? '0 0 12px #ffa726' : '0 0 12px #4facfe',
            }}
          />
        );
      })}

      {/* Card */}
      <motion.div
        initial={{ scale: 0.7, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.8, y: 10 }}
        transition={{ type: 'spring', damping: 18, stiffness: 220 }}
        className="relative glass-card rounded-3xl px-8 py-7 text-center max-w-sm mx-4"
        style={{ boxShadow: '0 30px 100px rgba(79,172,254,0.35), 0 30px 100px rgba(255,167,38,0.25)' }}
      >
        {/* Animated border ring */}
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 6, repeat: Infinity, ease: 'linear' }}
          className="absolute -inset-px rounded-3xl pointer-events-none"
          style={{
            background: 'conic-gradient(from 0deg, #4facfe, #ffa726, #4facfe)',
            mask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
            WebkitMask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
            WebkitMaskComposite: 'xor',
            maskComposite: 'exclude',
            padding: '1.5px',
            opacity: 0.7,
          }}
        />

        {/* Check icon */}
        <motion.div
          initial={{ scale: 0, rotate: -45 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: 'spring', damping: 12, stiffness: 200, delay: 0.15 }}
          className="mx-auto mb-4 w-16 h-16 rounded-full flex items-center justify-center"
          style={{
            background: 'linear-gradient(135deg, #4facfe, #ffa726)',
            boxShadow: '0 0 50px rgba(79,172,254,0.7), 0 0 80px rgba(255,167,38,0.5)',
          }}
        >
          <Check size={32} className="text-white" strokeWidth={3} />
        </motion.div>

        <div className="text-[10px] uppercase tracking-[0.25em] text-[#ffd54f] font-semibold">
          ⚡ a faísca da oportunidade ⚡
        </div>
        <div className="mt-2 heading-display text-3xl sm:text-4xl font-bold gradient-text">
          ⚡ DEU SPARK ⚡
        </div>
        <p className="mt-3 text-sm text-slate-300 leading-relaxed">
          Uma nova oportunidade começou. <br />
          Beatriz e Roberto agora podem conversar e fazer este sonho acontecer.
        </p>

        {/* Avatars */}
        <div className="mt-5 flex items-center justify-center gap-2">
          <div className="relative w-12 h-12 rounded-full overflow-hidden ring-2 ring-[#ffa726] glow-gold">
            <img src="https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=200&h=200&fit=crop&crop=face&q=80" alt="" className="w-full h-full object-cover" />
          </div>
          <div className="text-[#4facfe]">
            <Heart size={18} className="fill-[#4facfe]" />
          </div>
          <div className="relative w-12 h-12 rounded-full overflow-hidden ring-2 ring-[#4facfe] glow-blue">
            <img src="https://images.unsplash.com/photo-1560250097-0b93528c311a?w=200&h=200&fit=crop&crop=face&q=80" alt="" className="w-full h-full object-cover" />
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ============== 3 Simple Steps ==============
const Steps = () => {
  const steps = [
    { num: '01', icon: UserCircle2, title: 'Crie seu perfil', desc: 'Em menos de 2 minutos. Sem complicação.' },
    { num: '02', icon: Video, title: 'Mostre seu negócio em vídeo', desc: 'Um vídeo curto, do seu jeito, com sua história.' },
    { num: '03', icon: Sparkles, title: 'Nasce uma Spark', desc: 'Quando um investidor curte sua ideia, a Spark acontece.' },
  ];
  return (
    <div className="mt-20 grid sm:grid-cols-3 gap-5">
      {steps.map((s, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6, delay: i * 0.1 }}
          whileHover={{ y: -6 }}
          className="relative glass-card rounded-2xl p-6 spotlight-card card-hover"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${
              i === 1
                ? 'bg-gradient-to-br from-[#ffa726]/25 to-[#ff7043]/10 text-[#ffd54f]'
                : 'bg-gradient-to-br from-[#4facfe]/25 to-[#1e90ff]/10 text-[#4facfe]'
            }`}>
              <s.icon size={18} />
            </div>
            <div className={`text-2xl heading-display font-bold opacity-30 ${
              i === 1 ? 'text-[#ffa726]' : 'text-[#4facfe]'
            }`}>{s.num}</div>
          </div>
          <h3 className="text-lg font-semibold text-white">{s.title}</h3>
          <p className="mt-2 text-sm text-slate-400 leading-relaxed">{s.desc}</p>
          {i < 2 && (
            <div className="hidden sm:flex absolute top-1/2 -right-3 -translate-y-1/2 w-6 h-6 rounded-full bg-[#050816] border border-white/10 items-center justify-center z-10">
              <ArrowRight size={11} className="text-[#4facfe]" />
            </div>
          )}
        </motion.div>
      ))}
    </div>
  );
};

export default SparkExperienceSection;
